import { StepType } from "@redotech/redo-model/advanced-flow/steps";
import { assertNever } from "@redotech/util/type";
import { PartialFlowStep } from "../steps";

/**
 * Checks if connecting sourceStepId to targetStepId would create a cycle in the flow
 * @param steps All steps in the flow
 * @param sourceStepId The step we're connecting from
 * @param targetStepId The step we're connecting to
 * @returns true if connecting would create a cycle, false otherwise
 */
export function wouldCreateCycle(
  steps: PartialFlowStep[],
  sourceStepId: string,
  targetStepId: string,
): boolean {
  // If we're connecting to ourselves, that's a cycle
  if (sourceStepId === targetStepId) {
    return true;
  }

  // Check if there's a path from targetStepId back to sourceStepId
  // If such a path exists, connecting source to target would create a cycle
  return hasPathBetween(steps, targetStepId, sourceStepId);
}

/**
 * Checks if there's a path from startStepId to endStepId using BFS
 * @param steps All steps in the flow
 * @param startStepId Starting step
 * @param endStepId Ending step
 * @returns true if a path exists, false otherwise
 */
function hasPathBetween(
  steps: PartialFlowStep[],
  startStepId: string,
  endStepId: string,
): boolean {
  const visited = new Set<string>();
  const queue: string[] = [startStepId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;

    if (visited.has(currentId)) {
      continue;
    }
    visited.add(currentId);

    // If we reached the end step, a path exists
    if (currentId === endStepId) {
      return true;
    }

    // Find the current step
    const currentStep = steps.find((s) => s.id === currentId);
    if (!currentStep) {
      continue;
    }

    // Get all next step IDs from the current step
    const nextStepIds = getNextStepIds(currentStep);

    // Add all unvisited next steps to the queue
    for (const nextId of nextStepIds) {
      if (nextId && !visited.has(nextId)) {
        queue.push(nextId);
      }
    }
  }

  return false;
}

/**
 * Gets all possible next step IDs from a given step
 * @param step The step to get next IDs from
 * @returns Array of next step IDs
 */
function getNextStepIds(step: PartialFlowStep): string[] {
  const nextIds: string[] = [];

  switch (step.type) {
    case StepType.TRIGGER:
    case StepType.WAIT:
    case StepType.WAIT_FOR_EVENT:
    case StepType.SEND_EMAIL:
    case StepType.SEND_SMS:
    case StepType.SEND_WEBHOOK:
    case StepType.MANAGE_CUSTOMER_TAGS:
    case StepType.MANAGE_STATIC_SEGMENT:
    case StepType.RECOVER:
    case StepType.OMS_ACTION:
    case StepType.UPSELL:
    case StepType.DOWNSELL:
    case StepType.TRACKING_PAGE:
    case StepType.WORK_ORDER_WORK_STEP:
    case StepType.RULE_ACTIONS:
    case StepType.RULE_ACTIONS_V3:
    case StepType.PROCESS_RETURN:
      if ("nextId" in step && step.nextId) {
        nextIds.push(step.nextId);
      }
      break;

    case StepType.CONDITION:
    case StepType.OMS_CONDITION:
    case StepType.UPSELL_CONDITION:
    case StepType.RCO_CHECKOUT_PACKAGE_CONDITION:
    case StepType.RULE_CONDITIONS:
    case StepType.RULE_CONDITIONS_V3:
    case StepType.WORK_ORDER_CONDITION:
      if ("nextTrueId" in step && step.nextTrueId) {
        nextIds.push(step.nextTrueId);
      }
      if ("nextFalseId" in step && step.nextFalseId) {
        nextIds.push(step.nextFalseId);
      }
      break;

    case StepType.RCO_SEGMENT:
    case StepType.POST_PURCHASE_SEGMENT:
    case StepType.POST_PURCHASE_PAGE:
    case StepType.POST_PURCHASE_SPLIT:
    case StepType.AB_TEST:
    case StepType.RCO_SPLIT:
    case StepType.RCO_CHECKOUT_PACKAGE:
    case StepType.CHECKOUT_EXPERIENCE:
    case StepType.BLOCK_CHECKOUT:
    case StepType.DO_NOTHING:
      // These step types have no next steps
      break;
    default:
      assertNever(step);
  }

  return nextIds;
}
